<template>
  <div class="user-policy-update">
    <div class="container">
      <h1 class="title">使用者權益更新</h1>

      <div class="policy-content">
        <div class="policy-notice">
          <i class="bi bi-exclamation-triangle-fill warning-icon"></i>
          <h2>重要通知</h2>
          <div class="notice-text">
            <p>我們的使用者條款和隱私政策已經更新，請查看新的政策內容。</p>
          </div>
        </div>

        <div class="policy-links">
          <p>請點選查看我們的最新政策：</p>
          <div class="links">
            <router-link :to="{ name: 'user-policy' }" target="_blank" class="policy-link">
              <i class="bi bi-file-text"></i>
              使用者條款
            </router-link>
            <router-link :to="{ name: 'privacy' }" target="_blank" class="policy-link">
              <i class="bi bi-file-text"></i>
              隱私權政策
            </router-link>
          </div>
        </div>

        <div class="confirmation-section">
          <h3>是否同意更新使用者權益？</h3>
          <p class="confirmation-text">點擊「同意」即表示，</p>
          <p class="confirmation-text">您同意更新的條款和隱私政策。</p>
          <p class="confirmation-text">如果您拒絕將會被登出。</p>
          <p class="confirmation-text">需要接受條款才能繼續使用我們的服務。</p>
        </div>
      </div>

      <div class="action-buttons">
        <button type="button" class="decline-btn" @click="handleDecline" :disabled="isLoading">
          <i class="bi bi-x-circle"></i>
          拒絕並登出
        </button>

        <button type="button" class="accept-btn" @click="handleAccept" :disabled="isLoading">
          <i class="bi bi-check-circle"></i>
          同意並繼續
        </button>
      </div>

      <div class="alert" v-if="alertMessage">
        {{ alertMessage }}
      </div>
    </div>

    <Loading v-show="isLoading" />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { updateUserAgreement } from '@/api/apiService';
import Loading from '@/components/Loading.vue';

// Composables
const store = useStore();
const router = useRouter();

// Reactive data
const isLoading = ref(false);
const alertMessage = ref('');

// Methods
const handleAccept = async () => {
  isLoading.value = true;
  alertMessage.value = '';

  try {
    await updateUserAgreement();

    // Success - redirect to home or previous page
    alertMessage.value = '確認同意更新的權益，將會跳轉到首頁！';

    setTimeout(() => {
      router.push(`/${store.state.currentLanguage}/`);
    }, 1000);
  } catch (error) {
    console.error('Failed to update user agreement:', error);
    alertMessage.value = '更新失敗，請重試。';
  } finally {
    isLoading.value = false;
  }
};

const handleDecline = () => {
  isLoading.value = true;

  // Logout user
  store.dispatch('logout');

  // Redirect to login page
  setTimeout(() => {
    router.push(`/${store.state.currentLanguage}/member-login/login`);
  }, 500);
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.user-policy-update {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 80vh;
  padding: 40px 20px;

  .container {
    max-width: 600px;
    width: 100%;
  }

  h1.title {
    font-size: 40px;
    font-weight: 200;
    letter-spacing: 0.5rem;
    margin: 30px auto 20px auto;
    text-align: center;
    color: $main-W;
  }

  .policy-content {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 0px 20px 20px 20px;
    margin-bottom: 30px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);

    .policy-notice {
      text-align: center;
      margin-bottom: 30px;

      .warning-icon {
        font-size: 48px;
        color: #fff599;
        margin-bottom: 10px;
        margin-top: 10px;
      }

      h2 {
        color: $primary;
        margin-bottom: 15px;
        font-size: 24px;
      }

      .notice-text {
        p {
          color: $main-W;
          line-height: 1.6;
          font-size: 16px;
          margin: 0;
          text-align: center;
        }
      }
    }

    .policy-links {
      margin-bottom: 30px;

      > p {
        color: $main-W;
        margin-bottom: 20px;
        text-align: center;
      }

      .links {
        display: flex;
        justify-content: space-between;
        gap: 30px;
        max-width: 400px;
        margin: 0 auto;
      }

      .policy-link {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: $primary;
        text-decoration: none;
        padding: 12px 20px;
        border: 1px solid $primary;
        border-radius: 5px;
        transition: all 0.3s ease;
        flex: 1;
        text-align: center;

        &:hover {
          background-color: $primary;
          color: #12103d;
        }

        i {
          font-size: 16px;
        }
      }
    }

    .confirmation-section {
      text-align: center;

      h3 {
        color: $main-W;
        margin-bottom: 15px;
        font-size: 20px;
      }

      .confirmation-text {
        color: #c7c7c7;
        line-height: 1.6;
        font-size: 14px;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 20px;
    justify-content: center;
    margin-bottom: 30px;

    button {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 15px 30px;
      font-size: 16px;
      font-weight: 500;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
      min-width: 180px;
      justify-content: center;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      i {
        font-size: 18px;
      }
    }

    .decline-btn {
      background-color: transparent;
      color: #ff6b6b;
      border: 2px solid #ff6b6b;

      &:hover:not(:disabled) {
        background-color: #ff6b6b;
        color: white;
      }
    }

    .accept-btn {
      background-color: $primary;
      color: #12103d;

      &:hover:not(:disabled) {
        background-color: lighten($primary, 10%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba($primary, 0.3);
      }
    }
  }

  .alert {
    text-align: center;
    color: $primary;
    font-weight: 500;
    padding: 15px;
    background: rgba($primary, 0.1);
    border-radius: 5px;
    border: 1px solid rgba($primary, 0.3);
  }

  // Mobile responsive
  @media (max-width: 768px) {
    padding: 20px 15px;

    h1.title {
      font-size: 28px;
      margin: 20px auto 15px auto;
    }

    .policy-content {
      padding: 0px 20px 20px 20px;

      .policy-links .links {
        flex-direction: column;
        gap: 15px;
        max-width: 100%;
      }
    }

    .action-buttons {
      flex-direction: column;
      align-items: center;

      button {
        width: 100%;
        max-width: 250px;
      }
    }
  }
}
</style>
